import { Baby, Pill, HeartPulse, CalendarDays, PhoneCall, Calculator } from 'lucide-react';

/*
 * Texas Children's Hospital General OR rotation. Built from the Blackboard TCH Pediatric
 * Anesthesiology Rotation Quick Guide, the pediatric drug dosing sheet and the vital
 * signs tables. See docs/blackboard-tch.md.
 */

export const BB_DOC = (id) => `https://bcm.blackboard.com/ultra/courses/_129_1/document/${id}?view=content&state=view`;
const GUIDE = BB_DOC('_255103_1');
const DOSING = BB_DOC('_255106_1');
const VITALS = BB_DOC('_255108_1');

const dose = (rows) => ({ type: 'rows', rows });

export const tchOr = {
  accent: '#da2530',
  icon: Baby,
  tagline: 'Main campus ORs, call with the GOAT, pediatric doses',
  notesKey: 'notes:texas-childrens:or',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'calculator', title: 'Dose Calculator', subtitle: 'Weight-based, by drug', icon: Calculator, action: 'calculator', featured: true },
      { id: 'dosing', title: 'TCH Dosing Sheet', subtitle: 'Premeds, analgesics, relaxants, antibiotics, rescue', icon: Pill, action: 'guide:dosing' },
      { id: 'vitals', title: 'Vital Signs by Age', subtitle: 'Heart rate and blood pressure', icon: HeartPulse, action: 'guide:vitals' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'day', title: 'Your Day', subtitle: 'Before you start, first day, hours', icon: CalendarDays, action: 'guide:day' },
      { id: 'call', title: 'Call', subtitle: 'Weekday, weekend, the pain pager', icon: PhoneCall, action: 'guide:call' },
    ] },
  ],

  locations: {
    groups: [
      { id: 'or', title: 'Main campus', items: [
        { name: 'Main OR board runner', floor: 'Main OR', note: 'The GOAT. Check in before you leave each day' },
        { name: 'Anesthesia workstation', floor: 'Each OR', note: 'The intraoperative Omnicell. Access set up at orientation' },
      ] },
    ],
  },

  documents: {
    storageKey: 'tch-or-pinned-docs',
    groups: [
      { id: 'orientation', title: 'Orientation', items: [
        { title: 'Rotation quick guide', subtitle: 'Blackboard', url: GUIDE },
        { title: 'Pediatric drug dosing', subtitle: 'Blackboard', url: DOSING },
        { title: 'Pediatric vital signs', subtitle: 'Blackboard', url: VITALS },
      ] },
      { id: 'reading', title: 'Reading', items: [
        { title: 'Coté, A Practice of Anesthesia for Infants and Children', subtitle: 'Pages 50–67, 6th edition. TMC Library', url: 'https://www.library.tmc.edu' },
      ] },
    ],
  },

  guides: {
    dosing: {
      title: 'Pediatric Doses',
      intro: 'Suggested doses. The TCH formulary in Epic (Clinical References) and your attending decide.',
      segments: [
        { id: 'premed', label: 'Premeds', sections: [
          { title: 'Premedication', blocks: [{ type: 'table', cols: ['Drug', 'PO', 'IV', 'IM', 'Nasal'], rows: [
            ['Midazolam', '0.5 mg/kg, max 20 mg', '0.05–0.1 mg/kg', '', '0.2 mg/kg'],
            ['Diazepam', '0.05–0.2 mg/kg, max 10 mg', '0.05–0.2 mg/kg, max 10 mg', '', ''],
            ['Ketamine', '5–8 mg/kg', '0.5–1 mg/kg', '4–5 mg/kg', '3–5 mg/kg'],
            ['Dexmedetomidine', '', '0.5–1 mcg/kg', '', '1–2 mcg/kg'],
            ['Gabapentin', '10 mg/kg, max 600 mg', '', '', ''],
          ] }, { type: 'note', text: 'Consider atropine with IM ketamine.' }] },
        ] },
        { id: 'analgesia', label: 'Analgesia', sections: [
          { title: 'Opioids', blocks: [dose([
            { label: 'Fentanyl', value: '1–2 mcg/kg', note: 'IV, IM or nasal' },
            { label: 'Hydromorphone', value: '0.01–0.02 mg/kg' },
            { label: 'Morphine', value: '0.05–0.1 mg/kg' },
            { label: 'Methadone', value: '0.1–0.2 mg/kg' },
            { label: 'Remifentanil', value: '0.05–2 mcg/kg/min' },
            { label: 'Sufentanil', value: '0.3–2 mcg/kg/h' },
          ])] },
          { title: 'Non-opioid', blocks: [dose([
            { label: 'Acetaminophen', value: '10–15 mg/kg PO or IV', note: '20–40 mg/kg rectal' },
            { label: 'Ketorolac', value: '0.5 mg/kg IV, max 30 mg', note: '0.5–1 mg/kg IM' },
            { label: 'Ibuprofen', value: '10 mg/kg PO' },
            { label: 'Dexmedetomidine', value: '0.5–1 mcg/kg', note: 'Or 0.2–1 mcg/kg/h' },
            { label: 'Ketamine', value: '0.5–1 mg/kg', note: 'Or 5–20 mcg/kg/min' },
          ])] },
        ] },
        { id: 'relaxants', label: 'Relaxants', sections: [
          { title: 'Intubating doses', blocks: [dose([
            { label: 'Succinylcholine', value: '1–2 mg/kg IV', note: '4 mg/kg IM' },
            { label: 'Rocuronium', value: '0.6–1.2 mg/kg' },
            { label: 'Vecuronium', value: '0.1 mg/kg' },
            { label: 'Cisatracurium', value: '0.1–0.15 mg/kg' },
          ])] },
          { title: 'Reversal', confirm: true, blocks: [dose([
            { label: 'Neostigmine', value: '0.07 mg/kg' },
            { label: 'Glycopyrrolate', value: '0.01 mg/kg', note: 'Sheet reads 0.1 mg/kg' },
            { label: 'Sugammadex', value: 'Restricted', note: 'Discuss with the attending' },
          ])] },
          { title: 'Antiemetics', confirm: true, blocks: [dose([
            { label: 'Dexamethasone', value: '0.1–0.15 mg/kg' },
            { label: 'Ondansetron', value: '0.1 mg/kg, max 4 mg', note: 'Per-kg figure missing from the sheet' },
            { label: 'Metoclopramide', value: '0.1–0.15 mg/kg, max 10 mg' },
            { label: 'Promethazine', value: '0.25–1 mg/kg, max 25 mg', note: 'Over 2 years old' },
          ])] },
        ] },
        { id: 'abx', label: 'Antibiotics', sections: [
          { title: 'Per the OR antibiotic guideline for timing', blocks: [dose([
            { label: 'Ampicillin', value: '50 mg/kg' },
            { label: 'Cefazolin', value: '30 mg/kg' },
            { label: 'Cefoxitin', value: '30–40 mg/kg' },
            { label: 'Clindamycin', value: '10 mg/kg', note: 'Slowly' },
            { label: 'Gentamicin', value: '2.5 mg/kg, max 120 mg', note: 'Slowly' },
            { label: 'Piperacillin-tazobactam', value: '100 mg/kg', note: 'Piperacillin component' },
            { label: 'Vancomycin', value: '15 mg/kg', note: 'Slowly' },
          ])] },
        ] },
        { id: 'rescue', label: 'Rescue', sections: [
          { title: 'Rescue drugs', blocks: [dose([
            { label: 'Atropine', value: '20 mcg/kg IV or IM' },
            { label: 'Epinephrine', value: '10 mcg/kg', note: 'PEA or asystole' },
            { label: 'Phenylephrine', value: '1–10 mcg/kg', note: 'Titrate' },
            { label: 'Ephedrine', value: '0.1–0.2 mg/kg' },
            { label: 'Calcium chloride', value: '10 mg/kg' },
            { label: 'Calcium gluconate', value: '30 mg/kg' },
            { label: 'Sodium bicarbonate', value: '1 mEq/kg' },
            { label: 'Insulin', value: '0.1 U/kg' },
            { label: 'Dextrose', value: '0.5–1 g/kg', note: '2–4 mL/kg of D25' },
            { label: 'Racemic epinephrine', value: '0.05–0.1 mL/kg of 2.25% in 3 mL NS, max 0.5 mL', note: 'Nebulised' },
            { label: 'Naloxone', value: '1 mcg/kg', note: 'Repeat as needed' },
          ])] },
        ] },
      ],
      sources: [{ title: 'Pediatric drug dosing', href: DOSING, detail: 'Blackboard, sign-in required' }],
    },

    vitals: {
      title: 'Vital Signs by Age',
      sections: [
        { title: 'Awake ranges', blocks: [{ type: 'table', cols: ['Age', 'Heart rate', 'Systolic', 'Diastolic'], rows: [
          ['Premature', '120–170', '55–75', '35–45'],
          ['0–3 months', '100–150', '65–85', '45–55'],
          ['3–6 months', '90–120', '70–90', '50–65'],
          ['6–12 months', '80–120', '80–100', '55–65'],
          ['1–3 years', '70–110', '90–105', '55–70'],
          ['3–6 years', '65–110', '95–110', '60–75'],
          ['6–12 years', '60–95', '100–120', '60–75'],
          ['Over 12 years', '55–85', '110–135', '65–85'],
        ] }, { type: 'note', text: 'Lower asleep or under anesthesia. Coté and Lerman, 6th edition, tables 2.7 and 2.8, after Nelson Textbook of Pediatrics.' }] },
      ],
      sources: [{ title: 'Pediatric vital signs', href: VITALS, detail: 'Blackboard, sign-in required' }],
    },

    day: {
      title: 'Your Day',
      sections: [
        { title: 'Before you start', blocks: [{ type: 'bullets', items: [
          'Coté, pages 50–67, through the TMC library',
          'The dosing and vital signs sheets',
        ] }] },
        { title: 'First day', blocks: [{ type: 'bullets', items: [
          'Orientation at the time you are given: OR locations, room setup, call, lectures, expectations',
          'Hospital tour, Epic, anesthesia workstation access, TCH badge, TCH scrubs. TCH scrubs are required in the ORs',
        ] }] },
        { title: 'Hours', blocks: [{ type: 'bullets', items: [
          'Set up before the morning lecture',
          'First cases 07:30. Most Fridays 08:30',
          'Relieved 17:00–18:00 unless on call or the case is worth staying for',
          'Check in with the GOAT before leaving',
        ] }] },
      ],
      sources: [{ title: 'Rotation quick guide', href: GUIDE, detail: 'Blackboard, sign-in required' }],
    },

    call: {
      title: 'Call',
      sections: [
        { title: 'Weekdays', blocks: [{ type: 'bullets', items: [
          'First and second call arrive at noon and check in with the main OR GOAT. Relief and add-ons as directed',
          'Down to one room: second call preops tomorrow’s add-ons and carries the pain Voalte for calls and consults. First call finishes the add-ons in the last OR',
          'Both stay in house until 07:00. The attending may ask second call to help with airways overnight',
        ] }] },
        { title: 'Weekends', blocks: [{ type: 'bullets', items: [
          'Both arrive at 07:00. Second call takes the pain pager and sign-out from the pain attending around 11:00, answers pain pages and sees new consults',
          'At 19:00 the two swap roles',
          'Staying the full 24 hours pays $812 to each. To give up the paid shift, email the chief pediatric anesthesia fellow for a replacement; home call for those 12 hours is possible but you can still be called in',
          'Free time on second call goes to add-on preops and ICU consents',
          'Both get a post-call day',
        ] }] },
      ],
      sources: [{ title: 'Rotation quick guide', href: GUIDE, detail: 'Blackboard, sign-in required' }],
    },
  },
};
